import { Link } from "react-router-dom";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { HardHat, Check, X, ArrowRight } from "lucide-react";
import { SEO } from "@/lib/seo";
import { PublicFooter } from "./FAQ";
import { RelatedPages } from "@/components/RelatedPages";

// Keyword targets: "Simpro alternative NZ", "Simpro vs Fieldlink",
// "Simpro alternative New Zealand", "Tradify alternative" (catch),
// "ServiceM8 alternative NZ" (catch).
export default function SimproAlternativeNZ() {
  const compare = [
    { row: "Built for", simpro: "Mostly AU/UK trades, retrofitted for NZ", us: "NZ civil construction, from day one" },
    { row: "Pricing", simpro: "Per-module + setup consultant fees", us: "Flat per-user, no setup fees" },
    { row: "Support hours", simpro: "AU business hours (often UK-overflow)", us: "NZ-based, 9am–6pm NZT" },
    { row: "Holidays Act 2003 timesheets", simpro: "Manual workarounds", us: "Built-in, public holidays auto-applied" },
    { row: "SSSP generator", simpro: "Document upload only", us: "AI-generated draft in 90 seconds" },
    { row: "Live fleet tracking", simpro: "$$$ add-on", us: "Cartrack integration included" },
    { row: "Setup time", simpro: "6–12 weeks with a consultant", us: "Day 1: sign up, invite crew, log a timesheet" },
    { row: "Free trial", simpro: "Demo call required", us: "7 days, no card, instant access" },
  ];

  const faqs = [
    {
      q: "Why are NZ contractors switching from Simpro to Fieldlink?",
      a: "Three reasons we hear most: 1) Simpro's per-module pricing balloons fast — every extra feature is another invoice. 2) Setup is a 6-12 week consultant engagement, not a day-1 thing. 3) Holidays Act 2003 timesheet maths is manual or BYO add-on. Fieldlink is flat-priced, set up the same day, with NZ payroll rules built in.",
    },
    {
      q: "Can I export my data out of Simpro?",
      a: "Yes — Simpro lets you export jobs, customers and invoices as CSV. Fieldlink imports CSVs directly so a typical migration takes a few hours, not weeks.",
    },
    {
      q: "Does Fieldlink do quoting / variations / invoicing like Simpro?",
      a: "Yes — full quote → variation → invoice → payment chain, plus rate libraries, supplier bills and Xero export. The bits Simpro charges extra for.",
    },
    {
      q: "What about Tradify or ServiceM8?",
      a: "Tradify and ServiceM8 are great for sparkies and plumbers but were never built for civil. No SSSPs, no plant/RUC tracking, no dispatch board. Fieldlink fits civil/groundworks/roading crews much better.",
    },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };

  return (
    <div className="bg-[#f6f5f1] text-[#142635] min-h-screen">
      <SEO
        title="Simpro Alternative NZ — Cheaper, Faster, NZ-Made | Fieldlink"
        description="Looking for a Simpro alternative in New Zealand? Fieldlink is the NZ-made civil construction software — flat per-user pricing, no setup consultants, Holidays Act timesheets, AI SSSPs. 7-day free trial."
        path="/simpro-alternative-nz"
        jsonLd={jsonLd}
      />

      <Header />

      <section className="bg-[#f6f5f1] text-[#142635] border-b border-[#cbd3d5] py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="block text-[11px] font-mono font-bold uppercase tracking-[0.28em] text-[#a63c0b] mb-4">Side-by-side comparison</div>
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-none mb-5">
            The NZ Simpro<br /><span className="text-[#a63c0b]">Alternative</span>
          </h1>
          <p className="text-[#465562] text-base sm:text-lg max-w-2xl">
            Simpro charges per-module, makes you buy a setup consultant, and runs on Aussie business hours. Fieldlink is flat-priced, set up the same day, and built in NZ for NZ civil contractors.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" className="rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider px-6 py-3" data-testid="simpro-cta">Start free trial</Link>
            <Link to="/pricing" className="rounded-md border border-[#cbd3d5] bg-white hover:bg-white text-[#142635] font-bold uppercase tracking-wider px-6 py-3">See pricing</Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16">
        <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mb-3">Fieldlink vs Simpro at a glance</h2>
        <p className="text-[#52616c] max-w-2xl mb-10">Honest comparison from NZ contractors who've used both.</p>

        <div className="rounded-lg border border-[#cbd3d5] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white text-[#142635]">
              <tr>
                <th className="text-left p-3 font-bold uppercase tracking-wider"></th>
                <th className="text-left p-3 font-bold uppercase tracking-wider">Simpro</th>
                <th className="text-left p-3 font-bold uppercase tracking-wider bg-[#FF5F15]">Fieldlink</th>
              </tr>
            </thead>
            <tbody>
              {compare.map((r) => (
                <tr key={r.row} className="border-t border-[#cbd3d5]">
                  <td className="p-3 font-bold">{r.row}</td>
                  <td className="p-3 text-[#52616c]"><X className="w-4 h-4 inline text-[#52616c] mr-1" />{r.simpro}</td>
                  <td className="p-3 bg-[#FF5F15]/10"><Check className="w-4 h-4 inline text-[#a63c0b] mr-1" /><span className="font-bold">{r.us}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-[#e9eeec] border-y border-[#cbd3d5] py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mb-8">Switching is easier than you think</h2>
          <ol className="space-y-4">
            {[
              { n: "1", t: "Export your Simpro data", b: "CSV exports of jobs, customers and quotes. Takes 10 minutes from inside Simpro's Settings → Export." },
              { n: "2", t: "Import to Fieldlink", b: "Drop the CSVs into Fieldlink's importer. We handle the mapping. Most teams are running in under a day." },
              { n: "3", t: "Keep Simpro running for 30 days", b: "Run both side-by-side. When you're confident, cancel Simpro. We help you with the timing." },
              { n: "4", t: "Train your crew on Fieldlink", b: "The PWA installs on any phone — no app store hoops. Workers usually pick it up in 5 minutes." },
            ].map((s) => (
              <li key={s.n} className="flex gap-4 items-start rounded-lg bg-white border border-[#cbd3d5] p-5">
                <div className="w-10 h-10 bg-[#FF5F15] text-[#142635] flex items-center justify-center font-display font-black text-xl shrink-0">{s.n}</div>
                <div>
                  <div className="font-bold uppercase tracking-wider">{s.t}</div>
                  <div className="text-[#52616c] text-sm mt-1">{s.b}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mb-8">Common questions</h2>
        <div className="space-y-4">
          {faqs.map((f) => (
            <details key={f.q} className="rounded-lg border border-[#cbd3d5] bg-white p-4 group">
              <summary className="font-bold cursor-pointer list-none flex justify-between items-start">
                <span>{f.q}</span>
                <span className="text-[#a63c0b] ml-3 group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-[#465562] text-sm leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="bg-[#e9eeec] border-y border-[#cbd3d5] text-[#142635] py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mb-4">Try Fieldlink free for 7 days</h2>
          <p className="text-[#52616c] mb-8">No card. No setup consultant. Up and running today.</p>
          <Link to="/register" className="inline-block rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider px-8 py-4">Start free trial <ArrowRight className="w-4 h-4 inline ml-1" /></Link>
          <div className="mt-6 text-xs text-[#52616c]">
            <Link to="/job-management-nz" className="underline hover:text-[#142635]">Job management</Link> ·{" "}
            <Link to="/civil-construction-software-nz" className="underline hover:text-[#142635]">Civil construction software</Link> ·{" "}
            <Link to="/pricing" className="underline hover:text-[#142635]">Pricing</Link>
          </div>
        </div>
      </section>

      <RelatedPages current="/simpro-alternative-nz" />

      <PublicFooter />
    </div>
  );
}

function Header() {
  return (
      <MarketingHeader />
  );
}
